import type { ScheduleTenant } from './leaseScheduleEngine';

/**
 * ⭐⭐⭐ THE RECEIPT. What was checked before this analysis goes to a client, and what was not.
 *
 * Joseph, 2026-08-30: *"what if we add a way to double check the work before it gives us the
 * actual investment analysis"*. This is the deterministic floor of that, and the design rule is
 * the whole point:
 *
 * ⛔ **A CHECK THAT CAN ONLY SAY YES IS A DECORATION.** All the value is in refusing. A green tick
 * that appears 98% of the time makes a reader read *less* carefully while catching nothing, which
 * trades a reliable checker - him - for an unreliable one. So every check here can BLOCK, every
 * one names what to DO, and a check whose inputs are missing returns `attention`, never `pass`.
 *
 * ⛔ **NO MODEL CALLS.** Every rule below is arithmetic or a comparison against the extraction.
 * The one time this codebase asked a model to review its own output, the fact guard raised 24
 * flags and all 24 were false, and it separately deleted a true sentence. Traceability and prose
 * are a job for a model; NUMBERS are a job for assertions.
 *
 * ⭐ Every rule here is a defect that actually shipped. The four from 2026-08-30 are pinned by
 * name in `analysisPreflight.test.ts`, each with the deal it was measured on.
 */

export type PreflightStatus = 'pass' | 'attention' | 'blocking';

export interface PreflightCheck {
  id: string;
  /** What was checked, in the past tense, so the receipt reads as a record and not a promise. */
  label: string;
  status: PreflightStatus;
  /** Absent only on a pass. Says what is wrong AND what to do about it. */
  detail?: string;
}

export interface PreflightReceipt {
  checks: PreflightCheck[];
  passed: PreflightCheck[];
  attention: PreflightCheck[];
  blocking: PreflightCheck[];
  /** ⛔ False whenever anything blocks. The receipt refuses; it does not merely warn. */
  readyToSend: boolean;
}

export interface PreflightInput {
  /** The rent roll as extracted. Empty is normal - most properties have none. */
  tenants: ScheduleTenant[];
  /** `reconcileInPlaceIncome`: stated minus rent roll. Null when not comparable. */
  inPlaceDifference: number | null;
  inPlaceStated: number | null;
  inPlaceRentRoll: number | null;
  /** The contractual NOI schedule the engine built. `null` entries are years it refused. */
  noiSchedule: Array<number | null>;
  /**
   * ⛔ THE CONTRACTUAL RENT the leases state, year by year - `buildRentSchedule().annualRent`.
   *
   * 🚨 NOT the same as `noiSchedule`, and conflating them printed a FALSE SENTENCE on screen.
   * Jackson Mobility is a VACANT owner-user building with no lease at all, and the receipt told
   * the reader "this deal HAS a contractual schedule ... Apply the schedule." It does not and
   * there is nothing to apply: `noiScheduleFromRent` had still produced five numeric NOI years by
   * layering growth onto the in-place figure, and counting those read as contractual rent.
   * ⭐ **A schedule of NOI is not a schedule of CONTRACT.** The lease is the only thing that can
   * say a year is contractually known.
   */
  contractualRent: Array<number | null>;
  /** Whether the user pressed Apply, so the schedule actually prices the deal. */
  scheduleIsApplied: boolean;
  /** Which source `exitNoi` resolved to. */
  exitNoiSource: 'contract' | 'proforma' | 'growth';
  holdingPeriod: number;
  /** ISO. Hold year 1 starts here. */
  closeDate: string;
  /** False when the close is the 90-day default rather than a date he chose. */
  closeDateWasChosen: boolean;
  /** Set only when rollover has been modelled. */
  hasRolloverAssumptions: boolean;
  /** `property.operating_expenses` - stated by the OM or derived by the extractor. */
  propertyOperatingExpenses: number | null;
  /** What the analysis actually models. */
  modelledExpenses: number;
  goingInCapRate: number | null;
  exitCapRate: number | null;
  /** Months from the close with no income. 0 on every stabilised deal. */
  leaseUpMonths: number;
  /** Capitalised carry - taxes, insurance and debt service across the vacant months. */
  carryCosts: number;
}

const iso = (d: Date) => d.toISOString().slice(0, 10);
const money = (v: number) => '$' + Math.round(Math.abs(v)).toLocaleString('en-US');
const plusYears = (start: string, n: number) => {
  const d = new Date(start + 'T12:00:00Z');
  d.setUTCFullYear(d.getUTCFullYear() + n);
  return iso(d);
};

const pass = (id: string, label: string): PreflightCheck => ({ id, label, status: 'pass' });
const flag = (id: string, label: string, status: Exclude<PreflightStatus, 'pass'>, detail: string): PreflightCheck =>
  ({ id, label, status, detail });

export const analysisPreflight = (input: PreflightInput): PreflightReceipt => {
  const checks: PreflightCheck[] = [];
  const {
    tenants, noiSchedule, scheduleIsApplied, exitNoiSource, holdingPeriod, closeDate,
    hasRolloverAssumptions, propertyOperatingExpenses, modelledExpenses,
    goingInCapRate, exitCapRate, leaseUpMonths, carryCosts,
  } = input;

  const hasRoll = tenants.length > 0;
  /**
   * ⛔ From the LEASES, never from the NOI schedule - and a year counts only when the rent is
   * POSITIVE. Jackson Mobility's single "tenant" is the owner-occupant, with no rent, no start and
   * no end, so `buildRentSchedule` returns five years of contractual rent = **$0**. Five numbers,
   * not five nulls - so counting `typeof v === 'number'` said "5 years of contractual rent were
   * built" about a building with no lease. **Zero rent means no lease states anything**, and there
   * is nothing to apply and nothing for an exit to price off.
   */
  const contractualYears = input.contractualRent.filter(v => typeof v === 'number' && v > 0).length;
  const holdEnd = plusYears(closeDate, holdingPeriod);

  /**
   * 1. THE CAP BASIS TIES TO THE RENT ROLL.
   * ⛔ Never blocking. Mojave's $2,427 gap is REAL other income and the flag existing IS the
   * correct outcome there - only a human reading the OM can tell that from a stale headline.
   */
  if (!hasRoll || input.inPlaceDifference === null) {
    checks.push(flag('cap-basis', 'The cap basis ties to the rent roll', 'attention',
      hasRoll
        ? 'The rent roll and the stated income could not be compared, so the basis this cap is struck on is unverified.'
        : 'This property carries no rent roll, so the cap is struck on a stated figure nothing checks it against.'));
  } else if (Math.abs(input.inPlaceDifference) < 1) {
    checks.push(pass('cap-basis', 'The cap basis ties to the rent roll'));
  } else {
    /**
     * ⭐ A GAP THE SIZE OF A ROUNDING DIFFERENCE IS NAMED AS ONE, AND STILL RAISED.
     *
     * Five occurrences in this corpus - Sierra $1, El Paseo $5, Bakersfield $5, Kiddie $6, Long
     * Beach $1 - and every time the DOCUMENT was the imprecise side. ⛔ It is still `attention`,
     * never a pass and never absorbed: widening a tolerance to go green is the one thing the
     * reconciliation standard forbids outright. Saying which KIND of gap it is costs nothing and
     * stops the receipt crying wolf on a deal that is fine.
     */
    const rounding = Math.abs(input.inPlaceDifference) < 10;
    checks.push(flag('cap-basis', 'The cap basis ties to the rent roll', 'attention',
      `The rent roll says ${money(input.inPlaceRentRoll ?? 0)} and the document states ` +
      `${money(input.inPlaceStated ?? 0)} - a gap of ${money(input.inPlaceDifference)}. ` +
      (rounding
        ? `That is the size of a rounding difference in the OM itself, which is the fifth ` +
          `occurrence in this corpus and the document has been the imprecise side every time. ` +
          `Confirm it rather than absorbing it.`
        : `Read the OM: real other income belongs in Other Income, a stale or forward headline does not.`)));
  }

  /**
   * 2. EVERY ESCALATION DATE CAME FROM THE DOCUMENT.
   * 🚨 The Long Beach defect. The Station's dates are not in the OM - only "3% annual" - and a
   * derived date drove a rent the document never states. ⭐ Deriving is legitimate; deriving
   * SILENTLY is not.
   */
  const derived = tenants.filter(t => (t.escalations ?? []).some(e => e && e.date_basis === 'inferred'));
  if (!hasRoll) {
    checks.push(flag('stated-dates', 'Every escalation date came from the document', 'attention',
      'No rent roll, so there are no escalation dates to check and the schedule is a growth model.'));
  } else if (derived.length === 0) {
    checks.push(pass('stated-dates', 'Every escalation date came from the document'));
  } else {
    checks.push(flag('stated-dates', 'Every escalation date came from the document', 'attention',
      `${derived.map(t => t.tenant_name ?? 'a tenant').join(', ')}: the OM states the RATE and not ` +
      `the dates, so the step dates are derived from the lease commencement anniversary. ` +
      `Confirm against the executed lease before sending - a derived date is a guess with a schedule around it.`));
  }

  /**
   * 3. THE EXIT PRICES ON THE FINAL HOLD YEAR.
   * 🚨🚨 BLOCKING. The screen really does read "Exit NOI (Year 6)" until a schedule is applied,
   * and pricing a year you do not own off a GENERIC growth rate on a lease with contractual steps
   * is the error Joseph's own rulebook forbids on the income side.
   */
  if (exitNoiSource === 'contract') {
    checks.push(pass('exit-year', `The exit prices on hold year ${holdingPeriod}, the last year you own it`));
  } else if (contractualYears > 0) {
    checks.push(flag('exit-year', `The exit prices on hold year ${holdingPeriod}, the last year you own it`, 'blocking',
      `This deal HAS a contractual schedule and the exit is priced off the ${exitNoiSource} figure instead. ` +
      `That prices a year you do not own, at a generic rate, on a lease that states its own steps. Apply the schedule.`));
  } else {
    checks.push(flag('exit-year', `The exit prices on hold year ${holdingPeriod}, the last year you own it`, 'attention',
      `No contractual schedule exists for this deal, so the exit is priced off the ${exitNoiSource} figure. ` +
      `That is the shipped behaviour, not an error - but nothing in the lease supports the exit NOI.`));
  }

  /**
   * 4. A LEASE EXPIRING INSIDE THE HOLD CARRIES A ROLLOVER ASSUMPTION.
   * 🚨 BLOCKING. Without one the engine refuses those years and the schedule becomes a FLOOR -
   * then the exit prices off that floor. Banc and Mojave both moved multiple IRR points on this.
   */
  const expiring = tenants.filter(t => {
    const end = t.lease_end;
    return typeof end === 'string' && end >= closeDate && end <= holdEnd;
  });
  if (expiring.length === 0) {
    checks.push(pass('rollover', 'No lease expires inside the hold without an assumption'));
  } else if (hasRolloverAssumptions) {
    checks.push(pass('rollover', `${expiring.length} lease(s) expire inside the hold and rollover is modelled`));
  } else {
    checks.push(flag('rollover', 'A lease expiring inside the hold carries a rollover assumption', 'blocking',
      `${expiring.map(t => `${t.tenant_name ?? 'a tenant'} (${t.lease_end})`).join(', ')} expire before ` +
      `${holdEnd} and no rollover is modelled. Those years carry the remaining leases ONLY - a floor - ` +
      `and the exit is priced on it. Model the renewal, and cite the document that supports it.`));
  }

  /**
   * 5. LANDLORD EXPENSES REACH THE MODEL.
   * 🚨 BLOCKING - the Bakersfield defect. A property carrying a positive operating-expense total
   * that the analysis models at zero is a cap struck on gross rent.
   */
  const stated = propertyOperatingExpenses ?? 0;
  if (stated <= 0) {
    checks.push(pass('expenses-loaded', 'Landlord expenses reach the model'));
  } else if (modelledExpenses > 0) {
    checks.push(pass('expenses-loaded', 'Landlord expenses reach the model'));
  } else {
    checks.push(flag('expenses-loaded', 'Landlord expenses reach the model', 'blocking',
      `The property carries ${money(stated)} of operating expense and this analysis models ZERO. ` +
      `The cap is being struck on gross rent. Enter the expense, or say why the landlord carries nothing.`));
  }

  /**
   * 6. A LEASE THAT ESCALATES DOES NOT PRICE FLAT.
   * 🚨 BLOCKING - F2. "2% annually" arrived as one step plus a sentence, so schedules ran flat and
   * cost up to 2.61 points of IRR on deals whose leases plainly rise.
   */
  const escalates = tenants.some(t => (t.escalations ?? []).length > 0);
  const numeric = noiSchedule.filter((v): v is number => typeof v === 'number');
  const isFlat = numeric.length > 1 && numeric.every(v => Math.abs(v - numeric[0]) < 1);
  if (!escalates || !isFlat) {
    checks.push(pass('escalations-priced', 'A lease that escalates does not price flat'));
  } else {
    checks.push(flag('escalations-priced', 'A lease that escalates does not price flat', 'blocking',
      `The rent roll carries escalations and every modelled year is ${money(numeric[0])}. ` +
      `A recurring rule recorded as one step runs the schedule flat - check the extraction's ` +
      `recurrence before pricing this.`));
  }

  /**
   * 7. THE SCHEDULE ACTUALLY PRICES THE DEAL.
   * ⚠️ Applying is opt-in, so a freshly extracted deal runs on the growth model and UNDERSTATES
   * itself. Not blocking on its own - check 3 blocks the case that matters.
   */
  if (contractualYears === 0) {
    checks.push(pass('schedule-applied', 'No contractual schedule to apply'));
  } else if (scheduleIsApplied) {
    checks.push(pass('schedule-applied', `The contractual schedule prices the deal (${contractualYears} of ${holdingPeriod} years)`));
  } else {
    checks.push(flag('schedule-applied', 'The contractual schedule prices the deal', 'attention',
      `${contractualYears} year(s) of contractual rent were built and NOT applied, so this deal is ` +
      `running on the growth model. Press Apply to projections.`));
  }

  /**
   * 8. THE EXIT CAP DOES NOT COMPRESS.
   * ⭐ His rulebook: "Never assume cap compression on exit". Attention, not blocking - he is
   * allowed to make that call deliberately, but not silently.
   */
  if (goingInCapRate === null || exitCapRate === null) {
    checks.push(flag('exit-cap', 'The exit cap does not compress below the entry', 'attention',
      'One of the two caps is missing, so the exit assumption cannot be checked against the entry.'));
  } else if (exitCapRate >= goingInCapRate) {
    checks.push(pass('exit-cap', `The exit cap (${exitCapRate.toFixed(2)}%) does not compress below the entry (${goingInCapRate.toFixed(2)}%)`));
  } else {
    checks.push(flag('exit-cap', 'The exit cap does not compress below the entry', 'attention',
      `The exit is ${exitCapRate.toFixed(2)}% against a ${goingInCapRate.toFixed(2)}% entry. ` +
      `That assumes the market pays MORE for the asset five years older. Your rule is never to ` +
      `assume compression - say why, or widen it.`));
  }

  /**
   * 9. THE CLOSE DATE IS HIS, NOT A DEFAULT.
   * ⚠️ The 90-day default silently decides where every blended year falls, and it once stated
   * itself as fact over his real date.
   */
  checks.push(input.closeDateWasChosen
    ? pass('close-date', `Hold year 1 runs from ${closeDate}, the close you entered`)
    : flag('close-date', 'The close date is entered, not defaulted', 'attention',
        `Hold year 1 runs from ${closeDate}, a 90-day default. Every mid-year step blends against ` +
        `that date, so entering the real one changes the proforma.`));

  /**
   * 10. A LEASE-UP CARRIES A COST, AND IT IS NOT IN NOI.
   * ⛔ The lease-up model suspends income and its operating expenses together, so the vacant year
   * reads NOI $0 rather than a loss. That is right - the real carry is CAPITAL - but only if the
   * carry was actually entered. Left at zero, a vacant building appears to cost nothing to hold
   * for a year, which is the flattering direction. Joseph's own Jackson model carries $250,000.
   */
  if (leaseUpMonths <= 0) {
    checks.push(pass('lease-up-carry', 'No lease-up period to carry'));
  } else if (carryCosts > 0) {
    checks.push(pass('lease-up-carry', `${leaseUpMonths} months of lease-up, carried at ${money(carryCosts)}`));
  } else {
    checks.push(flag('lease-up-carry', 'A lease-up period carries a cost', 'blocking',
      `${leaseUpMonths} months of lease-up are modelled and Carry Costs are zero. The model shows ` +
      `NOI of $0 for those months, not a loss - so an empty building appears to cost nothing to ` +
      `hold. Enter the taxes, insurance and debt service across the vacant period as Carry Costs.`));
  }

  const passed = checks.filter(c => c.status === 'pass');
  const attention = checks.filter(c => c.status === 'attention');
  const blocking = checks.filter(c => c.status === 'blocking');
  return { checks, passed, attention, blocking, readyToSend: blocking.length === 0 };
};
